import { PropertyService } from '@/http-services/property.service';
import { PropertyRow } from 'common';
import { defineStore } from 'pinia';
import { computed, shallowRef, watch, watchEffect } from 'vue';
import { useAuth } from './auth.store';
import { UserService } from '@/http-services/user.service';
import { usePreferenceStorage } from '@/composables/use-preference-storage';
import { isPlatform } from '@ionic/vue';
import { Lorax } from '@ncss/models';

export const useUser = defineStore('user', () => {
  const auth = useAuth();
  const propertyService = new PropertyService();
  const userService = new UserService();
  const recentlyViewedProperties = shallowRef<PropertyRow[]>([]);
  const useMetric = computed(() => !!auth.user?.preferences?.useMetric);
  const { state: proximityScannerEnabled } = usePreferenceStorage('ncss-proximity-scanner-on', { default: true });
  const { state: barcodeSoundsEnabled } = usePreferenceStorage('ncss-barcode-sounds-on', { default: true });
  const { state: surpriseMeEnabled } = usePreferenceStorage('ncss-surprise-me', { default: false });
  const seenWizard = usePreferenceStorage('ncss-seen-permission-wizard', { default: false });
  const userId = computed(() => auth.user?._id);

  watch(
    () => auth.state,
    async (val) => {
      if (val === 'AUTHENTICATED') {
        recentlyViewedProperties.value = await propertyService.recentlyViewedProperties();
      } else {
        recentlyViewedProperties.value = [];
      }
    },
    { immediate: true }
  );

  async function viewedProperty(propertyId: string) {
    recentlyViewedProperties.value = await propertyService.viewedProperty(propertyId);
  }

  async function setUseMetric(useMetric: boolean) {
    const updatedUser = await userService.updatePreferences({ useMetric });
    if (updatedUser) {
      auth.setUser(updatedUser);
    }
  }

  async function shouldRedirectToPermissions(): Promise<boolean> {
    if (!isPlatform('hybrid')) {
      return false;
    }
    if (!seenWizard.initialized.value) {
      await seenWizard.waitForInit();
    }
    return !seenWizard.state.value;
  }

  function markPermissionWizardSeen() {
    seenWizard.state.value = true;
  }

  watchEffect(() => {
    console.log('Setting lorax useMetric', useMetric.value);
    Lorax.useMetric = useMetric.value;
  });

  return {
    recentlyViewedProperties,
    viewedProperty,
    useMetric,
    proximityScannerEnabled,
    barcodeSoundsEnabled,
    setUseMetric,
    shouldRedirectToPermissions,
    markPermissionWizardSeen,
    surpriseMeEnabled,
    userId,
  };
});
